import { useCallback, useMemo } from 'react';
import { defineMessages } from 'react-intl';
import { Box } from '@cbhq/cds-web/layout';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { EditMemberPendingRoleBanner } from '../components/EditMemberTray/EditMemberPendingRoleBanner';
import type { EditMemberStatus } from '../components/EditMemberTray/EditMemberTrayHeader';
import { MemberRecentActivity } from '../components/EditMemberTray/MemberRecentActivity';
import { ProductAccessGroup } from '../components/InviteTray/ProductAccessGroup';
import { ProtectedRoleWarning } from '../components/InviteTray/ProtectedRoleWarning';
import { useEditProtectedRoleWarning } from '../components/InviteTray/useProtectedRoleWarning';
import { StepUpTwoFactorModal } from '../components/StepUpTwoFactorModal';
import { UnsavedChangesAlert } from '../components/UnsavedChangesAlert';
import { useEditMemberSave } from '../hooks/useEditMemberSave';
import { getSelections } from '../hooks/useMemberEdit';
import { useRoles } from '../hooks/useRoles';
import { useSingleLEMemberAccess } from '../hooks/useSingleLEMemberAccess';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import type { Member as TeamMember } from '../utils/constants';
import type { PendingRole } from '../utils/gracePeriod';
import { isSingleEntityGatedRoleMutation } from '../utils/stepUp';
import { getMemberDisplayName } from '../utils/workspaceTeam';

import { EditMemberTray } from './EditMemberTray';

const EMPTY_ROLES_BY_ENTITY: Record<string, readonly string[]> = {};

const messages = defineMessages({
  saveButton: {
    id: 'Team.singleLEEditTray.saveButton',
    defaultMessage: 'Save changes',
    description: 'Save button in the single-LE edit member tray',
  },
  saveSuccess: {
    id: 'Team.singleLEEditTray.saveSuccess',
    defaultMessage: 'Membership updated',
    description: 'Toast shown after successfully saving member role changes',
  },
  saveError: {
    id: 'Team.singleLEEditTray.saveError',
    defaultMessage: 'Error updating membership',
    description: 'Toast shown when saving member role changes fails',
  },
});

type SingleLEEditTrayProps = {
  member: TeamMember;
  status: EditMemberStatus;
  joinedLabel?: string;
  lastActiveLabel?: string;
  isEditable?: boolean;
  /** The member's pending roles, for the pending role banner. */
  pendingRoles?: PendingRole[];
  /** Whether the current user manages an entity, i.e. may cancel a pending role there. */
  canEditEntity?: (legalEntityId: string) => boolean;
  onClose: () => void;
};

export function SingleLEEditTray({
  member,
  status,
  joinedLabel,
  lastActiveLabel,
  isEditable = true,
  pendingRoles,
  canEditEntity,
  onClose,
}: SingleLEEditTrayProps) {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';
  const { roleMap } = useRoles();

  const { selections, rows, hasChanges, legalEntityIds } = useSingleLEMemberAccess(member.roles);

  // Roles with no definition are preserved verbatim on save, since
  // updateOrganizationMemberRole() replaces the full per-entity set. Defined
  // roles live in `selections`, so excluding them here avoids a double-submit.
  const hiddenRoles = useMemo(
    () => member.roles.filter((role) => roleMap[role] === undefined),
    [member.roles, roleMap],
  );

  const selectedRoles = useMemo(() => getSelections(selections), [selections]);

  const submissionRoles = useMemo(
    () => [...selectedRoles, ...hiddenRoles],
    [hiddenRoles, selectedRoles],
  );

  // IDFE-4025 (t0018_4fba688a): warn when a protected role (e.g. Admin) is newly
  // selected. Roles already held or pending don't warn again.
  const singleEntityId = legalEntityIds[0];
  const selectedRolesByEntity = useMemo(
    () => (singleEntityId == null ? EMPTY_ROLES_BY_ENTITY : { [singleEntityId]: selectedRoles }),
    [selectedRoles, singleEntityId],
  );
  const currentRolesByEntity = useMemo(
    () => (singleEntityId == null ? EMPTY_ROLES_BY_ENTITY : { [singleEntityId]: member.roles }),
    [member.roles, singleEntityId],
  );
  const protectedRoleWarning = useEditProtectedRoleWarning(
    selectedRolesByEntity,
    currentRolesByEntity,
  );

  const canCancelPendingRole = useCallback(
    (legalEntityId: string) => isEditable && (canEditEntity?.(legalEntityId) ?? true),
    [canEditEntity, isEditable],
  );

  const isGatedMutation = useMemo(
    () => isSingleEntityGatedRoleMutation(member.roles, submissionRoles),
    [member.roles, submissionRoles],
  );

  const {
    footer,
    isSaving,
    stepUpModalProps,
    refreshWorkspaceMembers,
    setIsCancellingPendingRole,
  } = useEditMemberSave({
    organizationId,
    memberUuid: member.userUuid,
    selectedRoles: submissionRoles,
    legalEntityIds,
    hasChanges,
    isGatedRoleMutation: isGatedMutation,
    isEditable,
    messages,
    onClose,
  });

  // A failed cancel can still revoke some grants. Save stays blocked until the
  // refetch lands, so useMemberEdit re-seeds from server roles, not rolled-back ones.
  const handlePendingRoleCancelFailed = useCallback(
    () => refreshWorkspaceMembers(),
    [refreshWorkspaceMembers],
  );

  const name = getMemberDisplayName(member);

  const { trayRef, isAlertVisible, handleBeforeClose, handleKeepEditing, handleDiscard } =
    useUnsavedChangesGuard(hasChanges);

  const recentActivity = useMemo(
    () => <MemberRecentActivity organizationId={organizationId} memberUuid={member.userUuid} />,
    [member.userUuid, organizationId],
  );

  return (
    <>
      <EditMemberTray
        name={name}
        email={member.email}
        status={status}
        joinedLabel={joinedLabel}
        lastActiveLabel={lastActiveLabel}
        avatarSrc={member.avatarUrl}
        trayRef={trayRef}
        footer={footer}
        recentActivity={recentActivity}
        beforeClose={handleBeforeClose}
        onClose={onClose}
      >
        <ProductAccessGroup rows={rows} isEditable={isEditable} />
        {/* Cancelling updates member.roles, and useMemberEdit re-seeds from it. */}
        <EditMemberPendingRoleBanner
          memberUuid={member.userUuid}
          pendingRoles={pendingRoles}
          canCancelEntity={canCancelPendingRole}
          onCancelFailed={handlePendingRoleCancelFailed}
          onCancellingChange={setIsCancellingPendingRole}
          isCancelDisabled={isSaving}
        />
        {protectedRoleWarning != null && (
          <Box paddingTop={2}>
            <ProtectedRoleWarning
              roleName={protectedRoleWarning.roleName}
              gracePeriodMs={protectedRoleWarning.gracePeriodMs}
              context="edit"
            />
          </Box>
        )}
      </EditMemberTray>
      <StepUpTwoFactorModal {...stepUpModalProps} />
      <UnsavedChangesAlert
        variant="edit"
        visible={isAlertVisible}
        memberName={name}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
    </>
  );
}
